import React, { useEffect, useState } from 'react'
import LayoutHeader from '../../../../layout/LayoutHeader'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { setErrorAlert, setSuccessAlert, setLoader } from '../../../../store/alert/alertSlice'
import { postDataAPI, getDataAPI } from '../../../../utils/fetchData'
import DizeeInput from '../../../../components/DizeeComponent/DizeeInput'

export default function EmailTracking() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [trackedEmails, setTrackedEmails] = useState([])
    const [newEmail, setNewEmail] = useState('')
    const [newLabel, setNewLabel] = useState('')
    const [loading, setLoading] = useState(true)
    const [isAdding, setIsAdding] = useState(false)
    const [isSyncing, setIsSyncing] = useState(false)

    useEffect(() => {
        fetchTrackedEmails()
    }, [])

    const fetchTrackedEmails = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await getDataAPI('user/gmail/tracked-emails', token)
            
            if (response.status === 200) {
                setTrackedEmails(response.data.data.trackedEmails || [])
            }
        } catch (error) {
            console.error('Error fetching tracked emails:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSyncEmails = async () => {
        try {
            setIsSyncing(true)
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/gmail/trigger-sync', {}, token)
            
            if (response.status === 200) {
                const { syncedCount, newEmails, totalTracked } = response.data.data
                
                if (newEmails.length > 0) {
                    dispatch(setSuccessAlert(`Sync complete! Found ${newEmails.length} new email(s) from tracked contacts`))
                } else {
                    dispatch(setSuccessAlert(`Sync complete! Checked ${syncedCount}/${totalTracked} contacts - no new emails`))
                }
                
                // Refresh the tracked emails list to show updated counts
                fetchTrackedEmails()
            } else {
                dispatch(setErrorAlert('Failed to sync emails'))
            }
        } catch (error) {
            console.error('Error syncing emails:', error)
            dispatch(setErrorAlert('Error syncing emails'))
        } finally {
            setIsSyncing(false)
            dispatch(setLoader(false))
        }
    }

    const handleAddEmail = async () => {
        if (!newEmail.trim() || !newLabel.trim()) {
            dispatch(setErrorAlert('Please enter both email and label'))
            return
        }

        if (!isValidEmail(newEmail)) {
            dispatch(setErrorAlert('Please enter a valid email address'))
            return
        }

        try {
            setIsAdding(true)
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/gmail/add-tracked-email', {
                email: newEmail.trim().toLowerCase(),
                label: newLabel.trim(),
                priority: 'medium'
            }, token)
            
            if (response.status === 200) {
                setTrackedEmails([...trackedEmails, response.data.data.trackedEmail])
                setNewEmail('')
                setNewLabel('')
                dispatch(setSuccessAlert('Email contact added successfully'))
            } else {
                dispatch(setErrorAlert(response.data.message || 'Failed to add email contact'))
            }
        } catch (error) {
            console.error('Error adding tracked email:', error)
            dispatch(setErrorAlert('Error adding email contact'))
        } finally {
            setIsAdding(false)
            dispatch(setLoader(false))
        }
    }

    const handleRemoveEmail = async (emailId) => {
        try {
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/gmail/remove-tracked-email', {
                emailId
            }, token)
            
            if (response.status === 200) {
                setTrackedEmails(trackedEmails.filter(email => email.id !== emailId))
                dispatch(setSuccessAlert('Email contact removed successfully'))
            } else {
                dispatch(setErrorAlert('Failed to remove email contact'))
            }
        } catch (error) {
            console.error('Error removing tracked email:', error)
            dispatch(setErrorAlert('Error removing email contact'))
        } finally {
            dispatch(setLoader(false))
        }
    }

    const handleUpdatePriority = async (emailId, newPriority) => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/gmail/update-email-priority', {
                emailId,
                priority: newPriority
            }, token)
            
            if (response.status === 200) {
                setTrackedEmails(trackedEmails.map(email => 
                    email.id === emailId ? { ...email, priority: newPriority } : email
                ))
                dispatch(setSuccessAlert('Priority updated successfully'))
            }
        } catch (error) {
            console.error('Error updating priority:', error)
            dispatch(setErrorAlert('Error updating priority'))
        }
    }

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    }

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'bg-red-500'
            case 'medium': return 'bg-yellow-500'
            case 'low': return 'bg-green-500'
            default: return 'bg-gray-500'
        }
    }

    const getPriorityText = (priority) => {
        switch (priority) {
            case 'high': return 'High Priority'
            case 'medium': return 'Medium Priority'
            case 'low': return 'Low Priority'
            default: return 'Unknown'
        }
    }

    if (loading) {
        return (
            <LayoutHeader>
                <div className="w-[390px] flex flex-col justify-center items-center h-screen">
                    <div className="text-white">Loading email tracking...</div>
                </div>
            </LayoutHeader>
        )
    }

    return (
        <LayoutHeader>
            <div className="w-[390px] flex flex-col justify-start items-center relative z-[0]">
                <div className="px-4 my-[50px] flex w-full justify-between">
                    <p className="text-white">Email Contact Tracking</p>
                    <div className='flex gap-[20px]'>
                        <p onClick={() => navigate('/more/setting/gmail')} className='text-white cursor-pointer'>Go back</p>
                    </div>
                </div>

                {/* Add New Email Form */}
                <div className="w-full px-4 mb-6">
                    <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#ffffff1a]">
                        <h3 className="text-white text-sm font-medium mb-4">Add Email Contact to Track</h3>
                        
                        <div className="space-y-3">
                            <DizeeInput
                                placeholder="Email address (e.g., booking@venue.com)"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                type="email"
                            />
                            
                            <DizeeInput
                                placeholder="Label (e.g., Venue Booker, Record Label, Manager)"
                                value={newLabel}
                                onChange={(e) => setNewLabel(e.target.value)}
                            />
                            
                            <button
                                onClick={handleAddEmail}
                                disabled={isAdding || !newEmail.trim() || !newLabel.trim()}
                                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors"
                            >
                                {isAdding ? 'Adding...' : 'Add Email Contact'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Sync Button */}
                {trackedEmails.length > 0 && (
                    <div className="w-full px-4 mb-6">
                        <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#ffffff1a]">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h3 className="text-white text-sm font-medium">Sync Tracked Emails</h3>
                                    <p className="text-gray-400 text-xs">Check for new emails from your tracked contacts</p>
                                </div>
                                <button
                                    onClick={handleSyncEmails}
                                    disabled={isSyncing}
                                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors"
                                >
                                    {isSyncing ? 'Syncing...' : 'Sync Now'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tracked Emails List */}
                <div className="w-full px-4">
                    <div className="bg-[#1a1a1a] rounded-lg border border-[#ffffff1a]">
                        <div className="p-4 border-b border-[#ffffff1a]">
                            <h3 className="text-white text-sm font-medium">Tracked Email Contacts ({trackedEmails.length})</h3>
                            <p className="text-gray-400 text-xs mt-1">
                                These contacts will be prioritized in AI analysis and insights
                            </p>
                        </div>
                        
                        {trackedEmails.length === 0 ? (
                            <div className="p-4 text-center">
                                <p className="text-gray-400 text-sm">No email contacts tracked yet</p>
                                <p className="text-gray-500 text-xs mt-1">
                                    Add important contacts like venue bookers, record labels, or managers
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-[#ffffff1a]">
                                {trackedEmails.map((email) => (
                                    <div key={email.id} className="p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <p className="text-white text-sm font-medium truncate">
                                                        {email.email}
                                                    </p>
                                                    <div className={`w-2 h-2 rounded-full ${getPriorityColor(email.priority)}`}></div>
                                                </div>
                                                
                                                <p className="text-gray-300 text-xs mb-2">{email.label}</p>
                                                
                                                <div className="flex items-center gap-4 text-xs text-gray-400">
                                                    <span>Added: {new Date(email.createdAt).toLocaleDateString()}</span>
                                                    {email.lastEmailAt && (
                                                        <span>Last email: {new Date(email.lastEmailAt).toLocaleDateString()}</span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="flex flex-col gap-2 ml-3">
                                                <select
                                                    value={email.priority}
                                                    onChange={(e) => handleUpdatePriority(email.id, e.target.value)}
                                                    className="bg-[#2a2a2a] text-white text-xs px-2 py-1 rounded border border-[#ffffff1a]"
                                                >
                                                    <option value="high">High</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="low">Low</option>
                                                </select>
                                                
                                                <button
                                                    onClick={() => handleRemoveEmail(email.id)}
                                                    className="bg-red-600 hover:bg-red-700 text-white text-xs px-2 py-1 rounded transition-colors"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {email.emailCount > 0 && (
                                            <div className="mt-3 p-2 bg-[#2a2a2a] rounded text-xs">
                                                <p className="text-gray-300">
                                                    <span className="font-medium">{email.emailCount}</span> emails tracked
                                                    {email.lastSubject && (
                                                        <span className="text-gray-400 block mt-1">
                                                            Latest: "{email.lastSubject}"
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Help Section */}
                <div className="w-full px-4 mt-6">
                    <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#ffffff1a]">
                        <h3 className="text-white text-sm font-medium mb-2">How Email Tracking Works</h3>
                        <div className="text-gray-300 text-xs leading-relaxed space-y-2">
                            <p>• <strong>High Priority:</strong> Critical contacts like major venue bookers or record labels</p>
                            <p>• <strong>Medium Priority:</strong> Important industry contacts and regular collaborators</p>
                            <p>• <strong>Low Priority:</strong> General industry contacts and occasional communications</p>
                            <p className="mt-3 text-gray-400">
                                Tracked contacts help Dizee's AI provide better context and insights about your music industry communications.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </LayoutHeader>
    )
} 